import React from 'react'
import { observer } from 'mobx-react'
import { SingleItemSelector } from 'admin/staffing/components/WorkspacePostingDialog/SingleItemSelector'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { AddUserToTeamViewModel } from './AddUserToTeamViewModel'

interface AddUserToTeamDialogProps {
  vm: AddUserToTeamViewModel
}

export const AddUserToTeamDialog = observer(
  ({ vm }: AddUserToTeamDialogProps) => {
    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader bordered withCloseButton>
          <ModalSheetTitle>Add user to team</ModalSheetTitle>
        </ModalSheetHeader>

        <ModalSheetContent>
          <ModalSheetSection spaced>
            <Form id="addUserToTeam" onSubmit={vm.submit}>
              <FormRow>
                <FormGroupContainer
                  label="Workspace"
                  error={vm.validation.getError('workspace')}
                >
                  <SingleItemSelector
                    model={vm.workspace}
                    itemOptions={vm.availableWorkspaces}
                    itemPrefix={(item) => (
                      <Avatar
                        subject={{
                          avatar: item?.branding.logos.icon,
                        }}
                        size={24}
                      />
                    )}
                    onChange={(value) =>
                      vm.setWorkspace(value === undefined ? null : value)
                    }
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select workspace"
                    disabled={vm.submit.pending}
                    dataTest="add-user-to-team-workspace-selector"
                  />
                </FormGroupContainer>
              </FormRow>

              <FormRow>
                <FormGroupContainer
                  label="Team name"
                  help="To which team do you want to add the user?"
                  error={vm.validation.getError('team')}
                >
                  <SingleItemSelector
                    model={vm.team}
                    itemOptions={vm.availableTeams}
                    onChange={(value) =>
                      vm.setTeam(value === undefined ? null : value)
                    }
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select team"
                    disabled={vm.submit.pending}
                    dataTest="add-user-to-team-team-selector"
                  />
                </FormGroupContainer>
              </FormRow>

              <FormRow>
                <FormGroupContainer
                  label="Team role"
                  help="Which role should this user have?"
                  error={vm.validation.getError('teamRole')}
                >
                  <SingleItemSelector
                    model={vm.teamRole}
                    itemOptions={vm.teamRoles}
                    onChange={(value) =>
                      vm.setTeamRole(value === undefined ? null : value)
                    }
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select team role"
                    disabled={vm.submit.pending}
                    dataTest="add-user-to-team-team-role-selector"
                  />
                </FormGroupContainer>
              </FormRow>
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Cancel
            </Button>
            <Button
              submit
              form="addUserToTeam"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="add-user-to-team-submit"
            >
              Save
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)
